import React, { useEffect, useState } from 'react';
import { FormGroup, Input, Label } from 'reactstrap';
import './Allocation.css';
import {
  fetchActiveEvent,
  fetchAllExamEvents,
  fetchCenterDetails,
  generateAdmitCards,
  getAttendanceSheetDownloadUrl,
} from '../../Services/Employees';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';

const Allocation = () => {
  const [activeEvent, setActiveEvent] = useState({});
  const [events, setEvents] = useState([]);
  const [centerDetails, setCenterDetails] = useState([]);
  const [eventConfigId, setEventConfigId] = useState('');
  const [centerDetailsId, setCenterDetailsId] = useState('');
  const [examinationDate, setExaminationDate] = useState('');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchActiveEvent()
      .then((response) => {
        const eventData = response.data[0]?.eventMaster;
        const configId = response.data[0]?.idEventconfig;
        const examDate = response.data[0]?.examinationDate;
        if (eventData) {
          setActiveEvent(eventData);
        }
        if (configId) {
          setEventConfigId(String(configId));
        }
        if (examDate) {
          // examinationDate may arrive as "yyyy-MM-dd" or ISO timestamp
          const normalized = String(examDate).slice(0, 10);
          setExaminationDate(normalized);
        }
      })
      .catch((error) => {
        console.error(error);
      });

    fetchAllExamEvents()
      .then((response) => {
        setEvents(response.data.data || []);
      })
      .catch((error) => {
        console.error(error);
      });

    fetchCenterDetails()
      .then((response) => {
        setCenterDetails(response.data || []);
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  const handleGenerateAdmitCards = () => {
    if (!centerDetailsId) {
      withReactContent(Swal).fire({ icon: 'warning', text: 'Please select center details.' });
      return;
    }
    if (!examinationDate) {
      withReactContent(Swal).fire({ icon: 'warning', text: 'Please select examination date.' });
      return;
    }

    const payload = {
      centerDetailsId: Number(centerDetailsId),
      examinationDate,
    };

    if (eventConfigId) {
      payload.eventConfigId = Number(eventConfigId);
    }

    setGenerating(true);
    generateAdmitCards(payload)
      .then((response) => {
        withReactContent(Swal).fire({
          icon: 'success',
          text: response.data.message,
          allowOutsideClick: false,
        });
      })
      .catch((error) => {
        const message = error.response?.data?.message || 'Error generating admit cards';
        withReactContent(Swal).fire({
          icon: 'error',
          text: message,
          allowOutsideClick: false,
        });
      })
      .finally(() => setGenerating(false));
  };

  const getSelectedEventName = () => {
    if (eventConfigId) {
      const selectedEvent = events.find((item) => String(item.id) === String(eventConfigId));
      if (selectedEvent?.event) {
        return selectedEvent.event;
      }
    }
    return activeEvent.eventName || '';
  };

  const attendanceDownloadUrl = getAttendanceSheetDownloadUrl(getSelectedEventName());

  const handleDownloadAttendanceSheet = () => {
    const eventName = getSelectedEventName();
    if (!eventName) {
      withReactContent(Swal).fire({ icon: 'warning', text: 'Please select an event or ensure active event is loaded.' });
      return;
    }
    window.open(getAttendanceSheetDownloadUrl(eventName), '_blank', 'noopener,noreferrer');
  };

  const handleCopyAttendanceUrl = async () => {
    const eventName = getSelectedEventName();
    if (!eventName) {
      withReactContent(Swal).fire({ icon: 'warning', text: 'Please select an event or ensure active event is loaded.' });
      return;
    }
    const url = getAttendanceSheetDownloadUrl(eventName);
    try {
      await navigator.clipboard.writeText(url);
      withReactContent(Swal).fire({ icon: 'success', text: 'Download URL copied to clipboard.' });
    } catch (error) {
      withReactContent(Swal).fire({ icon: 'info', text: url });
    }
  };

  return (
    <div className="card border-0" style={{ background: 'white' }}>
      <h5 style={{ textAlign: 'center', color: 'Green', marginTop: '16px' }}>
        Active Event: <span style={{ fontSize: '15px', fontWeight: 'bold' }}>{activeEvent.eventName || '-'}</span>
      </h5>

      <div className="excel-upload-container" style={{ marginBottom: '16%', marginTop: '2%', width: '700px', textAlign: 'left' }}>
        <h2 className="upload-heading" style={{ textAlign: 'center' }}>Generate Admit Cards from Center Selection</h2>
        <p className="text-muted" style={{ fontSize: '14px' }}>
          Creates admit card records from paper center selection for the selected event.
        </p>

        <FormGroup>
          <Label for="eventConfigId">Event</Label>
          <Input
            id="eventConfigId"
            type="select"
            value={eventConfigId}
            onChange={(e) => setEventConfigId(e.target.value)}
          >
            <option value="">Use Active Event</option>
            {events.map((item) => (
              <option key={item.id} value={item.id}>
                {item.event}{item.eventActive ? ' (Active)' : ''}
              </option>
            ))}
          </Input>
        </FormGroup>

        <FormGroup>
          <Label for="centerDetailsId">Center Details</Label>
          <Input
            id="centerDetailsId"
            type="select"
            value={centerDetailsId}
            onChange={(e) => setCenterDetailsId(e.target.value)}
          >
            <option value="">Select Center Details</option>
            {centerDetails.map((item) => (
              <option key={item.id} value={item.id}>
                {item.centerName} ({item.centerCode})
              </option>
            ))}
          </Input>
        </FormGroup>

        <FormGroup>
          <Label for="examinationDate">Examination Date</Label>
          <Input
            id="examinationDate"
            type="date"
            value={examinationDate}
            onChange={(e) => setExaminationDate(e.target.value)}
          />
        </FormGroup>

        <div style={{ marginTop: '10px', textAlign: 'center' }}>
          <button type="button" className="upload-btn" onClick={handleGenerateAdmitCards} disabled={generating}>
            {generating ? 'Generating...' : 'Generate Admit Cards'}
          </button>
        </div>
      </div>

      <div className="excel-upload-container" style={{ marginBottom: '16%', marginTop: '2%', width: '700px', textAlign: 'left' }}>
        <h2 className="upload-heading" style={{ textAlign: 'center' }}>Download Attendance Sheet</h2>
        <p className="text-muted" style={{ fontSize: '14px' }}>
          Generates attendance PDFs by center and downloads a zip file for the selected event.
        </p>

        <FormGroup>
          <Label>Event for Attendance Sheet</Label>
          <Input type="text" value={getSelectedEventName() || '-'} readOnly />
        </FormGroup>

        <FormGroup>
          <Label>Direct Download URL</Label>
          <Input type="text" value={attendanceDownloadUrl || 'Select an event first'} readOnly />
        </FormGroup>

        <div style={{ marginTop: '10px', textAlign: 'center', display: 'flex', gap: '10px', justifyContent: 'center' }}>
          <button type="button" className="upload-btn" onClick={handleDownloadAttendanceSheet} disabled={!attendanceDownloadUrl}>
            Download Attendance Sheet
          </button>
          <button type="button" className="upload-btn" onClick={handleCopyAttendanceUrl} disabled={!attendanceDownloadUrl}>
            Copy URL
          </button>
        </div>
      </div>
    </div>
  );
};

export default Allocation;
